import { useState, useRef, useEffect } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { authFetch } from "../lib/authFetch";
import {
  Building2, MapPin, Users, Plus, Pencil, Trash2, Star, X,
  Check, Upload, Loader2, Phone, Mail, ChevronDown,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────────
interface ResellerFull {
  id: string;
  companyName: string;
  siren: string | null;
  siret: string | null;
  numeroTva: string | null;
  nbSalaries: number | null;
  formeJuridique: string | null;
  nomDirigeant: string | null;
  secteurActivite: string | null;
  couleurPrimaire: string;
  couleurSecondaire: string;
  logoUrl: string | null;
  siteWeb: string | null;
}

interface Adresse {
  id: string;
  label: string | null;
  adresse: string;
  codePostal: string | null;
  ville: string | null;
  pays: string | null;
  contactNom: string | null;
  contactTelephone: string | null;
  typeAdresse: string | null;
  isDefault: boolean;
}

interface PortailContact {
  id: string;
  prenom: string | null;
  nom: string | null;
  email: string;
  telephone: string | null;
  roleSociete: string | null;
  role: string;
  actif: boolean;
  avatarUrl: string | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const FORMES = ["SAS", "SARL", "EURL", "SA", "SNC", "Auto-entrepreneur", "Autre"];
const TYPES_ADRESSE = ["Livraison", "Facturation", "Les deux"];

function getInitials(prenom: string | null, nom: string | null) {
  return ((prenom?.[0] || "") + (nom?.[0] || "")).toUpperCase() || "?";
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, disabled, type = "text" }: {
  value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean; type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
    />
  );
}

function Select({ value, onChange, options, disabled }: {
  value: string; onChange: (v: string) => void; options: string[]; disabled?: boolean;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 appearance-none disabled:opacity-50"
      >
        <option value="">— Sélectionner —</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <ChevronDown className="absolute right-2.5 top-2.5 w-4 h-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}

// ── Modal générique ────────────────────────────────────────────────────────────
function Modal({ open, onClose, title, children }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

// ── Section Informations société ───────────────────────────────────────────────
function SectionInfos({ reseller, isAdmin, onSaved }: {
  reseller: ResellerFull; isAdmin: boolean; onSaved: (r: ResellerFull) => void;
}) {
  const { refreshBrand } = usePortailAuth();
  const [form, setForm] = useState({
    companyName: reseller.companyName || "",
    formeJuridique: reseller.formeJuridique || "",
    siret: reseller.siret || "",
    siren: reseller.siren || "",
    numeroTva: reseller.numeroTva || "",
    nbSalaries: reseller.nbSalaries?.toString() || "",
    secteurActivite: reseller.secteurActivite || "",
    nomDirigeant: reseller.nomDirigeant || "",
    siteWeb: reseller.siteWeb || "",
    couleurPrimaire: reseller.couleurPrimaire || "#2563eb",
    couleurSecondaire: reseller.couleurSecondaire || "#1e40af",
  });
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(reseller.logoUrl || "");
  const [toast, setToast] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };
  const set = (k: string) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    refreshBrand({ couleurPrimaire: form.couleurPrimaire, couleurSecondaire: form.couleurSecondaire });
  }, [form.couleurPrimaire, form.couleurSecondaire]);

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const res = await authFetch("/api/portail/my/reseller/logo", { method: "PUT", body: fd, headers: {} });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setLogoUrl(data.logoUrl);
      refreshBrand({ logoUrl: data.logoUrl });
      showToast("Logo mis à jour");
    } catch (err: any) {
      showToast("Erreur : " + err.message);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch("/api/portail/my/reseller", {
        method: "PUT",
        body: JSON.stringify({ ...form, nbSalaries: form.nbSalaries ? parseInt(form.nbSalaries) : null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      onSaved(data);
      refreshBrand({ companyName: data.companyName, couleurPrimaire: data.couleurPrimaire, couleurSecondaire: data.couleurSecondaire });
      showToast("Modifications enregistrées");
    } catch (err: any) {
      showToast("Erreur : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const primary = form.couleurPrimaire;

  return (
    <>
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-foreground text-background text-sm px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
          <Check className="w-4 h-4" /> {toast}
        </div>
      )}

      <div className="flex gap-5 items-start">
        {/* ── Colonne gauche : Informations société (2/3) ── */}
        <div className="flex-1 min-w-0 bg-card border border-border rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
            <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
            <h2 className="text-sm font-semibold text-foreground">Informations de la société</h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Field label="Raison sociale">
                  <Input value={form.companyName} onChange={set("companyName")} disabled={!isAdmin} placeholder="Nom de l'entreprise" />
                </Field>
              </div>
              <Field label="Forme juridique">
                <Select value={form.formeJuridique} onChange={set("formeJuridique")} options={FORMES} disabled={!isAdmin} />
              </Field>
              <Field label="Nom du dirigeant">
                <Input value={form.nomDirigeant} onChange={set("nomDirigeant")} disabled={!isAdmin} placeholder="Prénom Nom" />
              </Field>
              <Field label="SIRET">
                <Input value={form.siret} onChange={set("siret")} disabled={!isAdmin} placeholder="14 chiffres" />
              </Field>
              <Field label="SIREN">
                <Input value={form.siren} onChange={set("siren")} disabled={!isAdmin} placeholder="9 chiffres" />
              </Field>
              <Field label="N° TVA intracommunautaire">
                <Input value={form.numeroTva} onChange={set("numeroTva")} disabled={!isAdmin} placeholder="FR00 000000000" />
              </Field>
              <Field label="Nombre de salariés">
                <Input value={form.nbSalaries} onChange={set("nbSalaries")} type="number" disabled={!isAdmin} placeholder="0" />
              </Field>
              <Field label="Secteur d'activité">
                <Input value={form.secteurActivite} onChange={set("secteurActivite")} disabled={!isAdmin} placeholder="Ex: Commerce, Hôtellerie…" />
              </Field>
              <Field label="Site web">
                <Input value={form.siteWeb} onChange={set("siteWeb")} disabled={!isAdmin} placeholder="https://exemple.fr" />
              </Field>
            </div>
          </div>
        </div>

        {/* ── Colonne droite : Logo & Couleurs (1/3) ── */}
        <div className="w-72 shrink-0 bg-card border border-border rounded-xl overflow-hidden flex flex-col">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
            <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
            <h2 className="text-sm font-semibold text-foreground">Logo & Couleurs</h2>
          </div>

          <div className="p-5 flex flex-col gap-5 flex-1">
            {/* Logo centré */}
            <div className="flex flex-col items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="w-20 h-20 object-contain rounded-lg border border-border" />
              ) : (
                <div
                  className="w-20 h-20 rounded-lg flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: primary }}
                >
                  {form.companyName.slice(0, 2).toUpperCase()}
                </div>
              )}
              {isAdmin && (
                <>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={logoUploading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors disabled:opacity-50"
                  >
                    {logoUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {logoUploading ? "Upload…" : "Changer le logo"}
                  </button>
                </>
              )}
            </div>

            {/* Séparateur */}
            <div className="border-t border-border" />

            {/* Couleurs */}
            <div className="flex flex-col gap-3">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Couleurs marque blanche</span>
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground">Couleur principale</span>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded border border-border" style={{ backgroundColor: form.couleurPrimaire }} />
                  {isAdmin ? (
                    <input
                      type="color"
                      value={form.couleurPrimaire}
                      onChange={e => set("couleurPrimaire")(e.target.value)}
                      className="w-8 h-6 rounded cursor-pointer border-0 bg-transparent"
                    />
                  ) : (
                    <span className="text-xs font-mono text-muted-foreground">{form.couleurPrimaire}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground">Couleur secondaire</span>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded border border-border" style={{ backgroundColor: form.couleurSecondaire }} />
                  {isAdmin ? (
                    <input
                      type="color"
                      value={form.couleurSecondaire}
                      onChange={e => set("couleurSecondaire")(e.target.value)}
                      className="w-8 h-6 rounded cursor-pointer border-0 bg-transparent"
                    />
                  ) : (
                    <span className="text-xs font-mono text-muted-foreground">{form.couleurSecondaire}</span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <p className="text-xs text-muted-foreground">L'aperçu dans la barre latérale se met à jour en temps réel.</p>
              )}
            </div>
          </div>

          {/* Bouton Enregistrer — uniquement dans cette carte */}
          {isAdmin && (
            <div className="px-5 pb-5">
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-white rounded-lg transition-colors disabled:opacity-60"
                style={{ backgroundColor: primary }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {saving ? "Enregistrement…" : "Enregistrer les modifications"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Section Adresses ───────────────────────────────────────────────────────────
function SectionAdresses({ resellerId, isAdmin, primary }: { resellerId: string; isAdmin: boolean; primary: string }) {
  const [adresses, setAdresses] = useState<Adresse[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Adresse | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    label: "", adresse: "", codePostal: "", ville: "",
    pays: "France", contactNom: "", contactTelephone: "",
    typeAdresse: "Livraison", isDefault: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/portail/my/adresses");
      if (res.ok) setAdresses(await res.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ label: "", adresse: "", codePostal: "", ville: "", pays: "France", contactNom: "", contactTelephone: "", typeAdresse: "Livraison", isDefault: false });
    setModalOpen(true);
  };

  const openEdit = (a: Adresse) => {
    setEditing(a);
    setForm({
      label: a.label || "", adresse: a.adresse, codePostal: a.codePostal || "",
      ville: a.ville || "", pays: a.pays || "France", contactNom: a.contactNom || "",
      contactTelephone: a.contactTelephone || "", typeAdresse: a.typeAdresse || "Livraison",
      isDefault: a.isDefault,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editing ? `/api/portail/my/adresses/${editing.id}` : "/api/portail/my/adresses";
      const res = await authFetch(url, { method: editing ? "PUT" : "POST", body: JSON.stringify(form) });
      if (res.ok) { setModalOpen(false); load(); }
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Supprimer cette adresse ?")) return;
    await authFetch(`/api/portail/my/adresses/${id}`, { method: "DELETE" });
    load();
  };

  const handleSetDefault = async (id: string) => {
    await authFetch(`/api/portail/my/adresses/${id}/default`, { method: "PUT" });
    load();
  };

  const s = (k: string) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Adresses</h2>
          <span className="text-xs text-muted-foreground">({adresses.length})</span>
        </div>
        {isAdmin && (
          <button onClick={openNew} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded-lg transition-colors" style={{ backgroundColor: primary }}>
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        )}
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : adresses.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">Aucune adresse enregistrée</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {adresses.map(a => (
              <div key={a.id} className="border border-border rounded-lg p-4 bg-background flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col gap-0.5">
                    {a.label && <p className="text-sm font-medium text-foreground">{a.label}</p>}
                    <p className="text-sm text-foreground">{a.adresse}</p>
                    {(a.codePostal || a.ville) && (
                      <p className="text-xs text-muted-foreground">{[a.codePostal, a.ville].filter(Boolean).join(" ")} — {a.pays}</p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {a.isDefault && (
                      <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                        <Star className="w-3 h-3" /> Par défaut
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <span className="text-xs px-2 py-0.5 rounded border border-border text-muted-foreground">{a.typeAdresse || "Livraison"}</span>
                </div>
                {(a.contactNom || a.contactTelephone) && (
                  <div className="text-xs text-muted-foreground flex flex-col gap-0.5 border-t border-border pt-2 mt-1">
                    {a.contactNom && <span>Contact : {a.contactNom}</span>}
                    {a.contactTelephone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.contactTelephone}</span>}
                  </div>
                )}
                {isAdmin && (
                  <div className="flex gap-2 mt-1 pt-2 border-t border-border">
                    {!a.isDefault && (
                      <button onClick={() => handleSetDefault(a.id)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                        <Star className="w-3.5 h-3.5" /> Par défaut
                      </button>
                    )}
                    <button onClick={() => openEdit(a)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground ml-auto">
                      <Pencil className="w-3.5 h-3.5" /> Modifier
                    </button>
                    <button onClick={() => handleDelete(a.id)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Modifier l'adresse" : "Nouvelle adresse"}>
        <Field label="Libellé"><Input value={form.label} onChange={s("label")} placeholder="Ex: Siège social, Entrepôt Lyon" /></Field>
        <Field label="Adresse *"><Input value={form.adresse} onChange={s("adresse")} placeholder="Rue, numéro…" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code postal"><Input value={form.codePostal} onChange={s("codePostal")} placeholder="75001" /></Field>
          <Field label="Ville"><Input value={form.ville} onChange={s("ville")} placeholder="Paris" /></Field>
        </div>
        <Field label="Pays"><Input value={form.pays} onChange={s("pays")} placeholder="France" /></Field>
        <Field label="Type d'adresse"><Select value={form.typeAdresse} onChange={s("typeAdresse")} options={TYPES_ADRESSE} /></Field>
        <Field label="Nom du contact sur place"><Input value={form.contactNom} onChange={s("contactNom")} placeholder="Prénom Nom" /></Field>
        <Field label="Téléphone du contact"><Input value={form.contactTelephone} onChange={s("contactTelephone")} placeholder="+33 6 00 00 00 00" /></Field>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.isDefault} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} className="rounded" />
          <span className="text-sm text-foreground">Définir comme adresse par défaut</span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:text-foreground transition-colors">Annuler</button>
          <button onClick={handleSave} disabled={saving || !form.adresse} className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-60 transition-colors" style={{ backgroundColor: primary }}>
            {saving ? "Enregistrement…" : editing ? "Modifier" : "Ajouter"}
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ── Section Contacts ───────────────────────────────────────────────────────────
function SectionContacts({ resellerId, isAdmin, primary }: { resellerId: string; isAdmin: boolean; primary: string }) {
  const [contacts, setContacts] = useState<PortailContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "", prenom: "", nom: "", telephone: "", roleSociete: "", role: "contact",
  });
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/portail/my/users");
      if (res.ok) setContacts(await res.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleToggle = async (id: string) => {
    await authFetch(`/api/portail/my/users/${id}/toggle`, { method: "PUT" });
    load();
  };

  const handleDelete = async (id: string, email: string) => {
    if (!confirm(`Supprimer le compte de ${email} ?`)) return;
    await authFetch(`/api/portail/my/users/${id}`, { method: "DELETE" });
    load();
  };

  const handleInvite = async () => {
    setSending(true);
    setInviteError(null);
    try {
      const res = await authFetch("/api/portail/my/users/invite", { method: "POST", body: JSON.stringify(inviteForm) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setInviteSuccess(true);
      setTimeout(() => { setModalOpen(false); setInviteSuccess(false); setInviteForm({ email: "", prenom: "", nom: "", telephone: "", roleSociete: "", role: "contact" }); }, 2000);
      load();
    } catch (err: any) {
      setInviteError(err.message);
    } finally { setSending(false); }
  };

  const si = (k: string) => (v: string) => setInviteForm(f => ({ ...f, [k]: v }));

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Users className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Contacts</h2>
          <span className="text-xs text-muted-foreground">({contacts.length})</span>
        </div>
        {isAdmin && (
          <button onClick={() => setModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded-lg transition-colors" style={{ backgroundColor: primary }}>
            <Plus className="w-3.5 h-3.5" /> Inviter un contact
          </button>
        )}
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">Aucun contact</div>
        ) : (
          <div className="divide-y divide-border">
            {contacts.map(c => (
              <div key={c.id} className="flex items-center gap-4 py-3">
                {/* Avatar */}
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                  style={{ backgroundColor: c.actif ? primary : "#9ca3af" }}
                >
                  {c.avatarUrl
                    ? <img src={c.avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover" />
                    : getInitials(c.prenom, c.nom)
                  }
                </div>

                {/* Infos */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground truncate">
                      {[c.prenom, c.nom].filter(Boolean).join(" ") || c.email}
                    </p>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                      c.role === "admin"
                        ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400"
                        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    }`}>
                      {c.role === "admin" ? "Admin" : "Contact"}
                    </span>
                    {!c.actif && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Inactif</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                    {c.telephone && <span className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{c.telephone}</span>}
                    {c.roleSociete && <span className="text-xs text-muted-foreground">{c.roleSociete}</span>}
                  </div>
                </div>

                {/* Actions */}
                {isAdmin && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleToggle(c.id)}
                      className={`text-xs px-2 py-1 rounded border transition-colors ${
                        c.actif
                          ? "border-border text-muted-foreground hover:text-foreground"
                          : "border-green-300 text-green-600 hover:bg-green-50"
                      }`}
                    >
                      {c.actif ? "Désactiver" : "Activer"}
                    </button>
                    <button
                      onClick={() => handleDelete(c.id, c.email)}
                      className="p-1.5 rounded text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => { setModalOpen(false); setInviteError(null); setInviteSuccess(false); }} title="Inviter un contact">
        {inviteSuccess ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-green-100 text-green-600">
              <Check className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-foreground">Invitation envoyée !</p>
            <p className="text-xs text-muted-foreground">Le contact recevra un email d’invitation avec les instructions pour accéder au portail.</p>
          </div>
        ) : (
          <>
            {inviteError && (
              <div className="px-3 py-2 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg">{inviteError}</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prénom"><Input value={inviteForm.prenom} onChange={si("prenom")} placeholder="Prénom" /></Field>
              <Field label="Nom"><Input value={inviteForm.nom} onChange={si("nom")} placeholder="Nom" /></Field>
            </div>
            <Field label="Email *"><Input value={inviteForm.email} onChange={si("email")} placeholder="contact@societe.fr" type="email" /></Field>
            <Field label="Téléphone"><Input value={inviteForm.telephone} onChange={si("telephone")} placeholder="+33 6 00 00 00 00" /></Field>
            <Field label="Fonction dans la société"><Input value={inviteForm.roleSociete} onChange={si("roleSociete")} placeholder="Directeur, Responsable achat…" /></Field>
            <Field label="Rôle portail">
              <Select value={inviteForm.role} onChange={si("role")} options={["admin", "contact"]} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:text-foreground transition-colors">Annuler</button>
              <button onClick={handleInvite} disabled={sending || !inviteForm.email} className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-60 transition-colors" style={{ backgroundColor: primary }}>
                {sending ? "Envoi…" : "Envoyer l'invitation"}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ── Page principale ────────────────────────────────────────────────────────────
export default function SocietePage() {
  const { portailUser, brand } = usePortailAuth();
  const [reseller, setReseller] = useState<ResellerFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = portailUser?.role === "admin";
  const primary = brand?.couleurPrimaire || "#2563eb";

  useEffect(() => {
    authFetch("/api/portail/my/reseller")
      .then(r => r.json())
      .then(d => { if (d.id) setReseller(d); else setError(d.message || "Erreur"); })
      .catch(() => setError("Impossible de charger les données"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !reseller) {
    return (
      <div className="p-8 text-center text-sm text-red-500">{error || "Erreur chargement"}</div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Profil de la société</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {isAdmin ? "Consultez et modifiez les informations de votre entreprise." : "Informations de votre entreprise (lecture seule)."}
        </p>
      </div>

      <SectionInfos
        reseller={reseller}
        isAdmin={isAdmin}
        onSaved={setReseller}
      />

      <SectionAdresses
        resellerId={portailUser!.resellerId}
        isAdmin={isAdmin}
        primary={primary}
      />

      <SectionContacts
        resellerId={portailUser!.resellerId}
        isAdmin={isAdmin}
        primary={primary}
      />
    </div>
  );
}
